import { useTranslation } from 'next-i18next';
import { TrashIcon } from '@heroicons/react/24/outline';
import { DestructiveAction } from '@components/common/DestructiveAction/DestructiveAction';
import { useSwrDeleteJobApplication } from '@hooks/useSwrApi/endpoints/job';

// Interface
// ******************************************
interface CardJobTalentDeleteApplicationProps {
  jobSlug: string,
  locale: Locale,
  onDeleted?: (jobSlug: string) => void,
}

/**
 * @description Delete button for a job application, shown on the applied jobs cards next to the 'already applied' message.
 * The backend only flags an application as deletable (`applicationDeletable`) for an admin impersonating
 * the talent, while the application is unviewed by the recruiter and at most 3 days old.
 *
 * - jobSlug: *string* - The slug of the applied job. Required.
 * - locale: *Locale* - The current app locale. Required.
 * - onDeleted: *function* - Callback executed after the application was deleted. Optional.
 */
export const CardJobTalentDeleteApplication = (props: CardJobTalentDeleteApplicationProps) => {
  const { jobSlug, locale, onDeleted } = props;

  // Translation
  const { t } = useTranslation('common');

  // SWR mutation
  const { trigger, isMutating } = useSwrDeleteJobApplication(
    jobSlug,
    locale,
    () => onDeleted?.(jobSlug),
    t('applied.jobs.delete.success'),
  );

  // Render component
  // *****************************************
  return (
    <DestructiveAction
      withConfirmation
      confirmationDescription={t('applied.jobs.delete.confirmation')}
      confirmText={t('global.action.delete')}
      onConfirm={async () => { await trigger(); }}
      isConfirmLoading={isMutating}
      type="button"
      styling="soft"
      color="error"
      rounding="2xl"
      size="xs"
      className="flex shrink-0 items-center justify-center text-xs!"
      isLoading={isMutating}
      title={t('global.action.delete')}
      aria-label={t('global.action.delete')}
    >
      <TrashIcon className="size-5" />
    </DestructiveAction>
  );
};
